import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import constants from '../constants';
import { CLIENT_APP } from '../constants/clientApp';

export const CORRELATION_ID_HEADER = 'X-Correlation-Id';
export const CLIENT_APP_HEADER = 'X-Client-App';

const OVERRIDE_COOKIE = 'gr-correlation-id';

// What core accepts as an id (letters, digits, . _ -, max 64). Anything else is ignored here rather than sent, so a
// stray or hostile cookie cannot make a request fail (XHR rejects header values with control or non-Latin1 characters).
const VALID_ID = /^[A-Za-z0-9._-]{1,64}$/;

let pageCorrelationId: string | undefined;

const readOverride = (): string | undefined => {
  const prefix = `${OVERRIDE_COOKIE}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  const value = cookie?.slice(prefix.length);
  return value && VALID_ID.test(value) ? value : undefined;
};

/**
 * The correlation id the browser sends to core (X-Correlation-Id), so one user flow can be followed through the API and
 * the functions. One id per page load, shared by every call the page makes. To follow a bug seen in production, set the
 * gr-correlation-id cookie (devtools: document.cookie = 'gr-correlation-id=bug-1234; path=/') and every call carries
 * that value instead.
 */
export const getCorrelationId = (): string => {
  const override = readOverride();
  if (override) {
    return override;
  }
  pageCorrelationId ??= uuidv4();
  return pageCorrelationId;
};

// Only our own API gets the headers; third-party hosts would fail their CORS preflight on an unknown header.
const isApiUrl = (url: string) =>
  url.startsWith(`${constants.baseApiHost}/api`) || url.startsWith('/api');

/**
 * Adds the two headers to every call the app makes to our API: the default axios instance and fetch (which
 * redux-api-middleware also uses). Call once at startup.
 */
export const installTrackingHeaders = () => {
  axios.interceptors.request.use((config) => {
    if (isApiUrl(config.url ?? '')) {
      config.headers.set(CORRELATION_ID_HEADER, getCorrelationId());
      config.headers.set(CLIENT_APP_HEADER, CLIENT_APP);
    }
    return config;
  });

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const url = input instanceof Request ? input.url : String(input);
    if (!isApiUrl(url)) {
      return originalFetch(input, init);
    }
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    );
    headers.set(CORRELATION_ID_HEADER, getCorrelationId());
    headers.set(CLIENT_APP_HEADER, CLIENT_APP);
    return originalFetch(input, { ...init, headers });
  };
};
